import { buildAuthedHeaders } from '@/lib/api-client'
import { env } from '@/lib/env'

export const TASK_TYPES = [
  'strategiedokument',
  'praesentation',
  'angebot',
  'linkedin_post',
  'linkedin_carousel',
  'newsletter',
  'blog_artikel',
  'data_analysis',
  'fachartikel',
  'lead_magnet',
  'one_pager',
  'press_release',
  'ai_bildprompt',
  'x_post',
] as const
export type TaskType = (typeof TASK_TYPES)[number]
export type OutputFormat = 'docx' | 'pptx' | 'pdf' | 'markdown' | 'json'
export type PlatformSkill = 'linkedin' | 'x' | 'newsletter' | 'blog' | 'presentation' | 'data-analysis'
export type PlatformOutputMode = 'create' | 'coach' | 'plan'
export type PresentationAspect = '16:9' | '4:5' | '1:1'

export interface LinkedinPostPayload {
  post: string
  first_comment: string
  hashtags: string[]
}

export interface XPostPayload {
  thread: string[]
  single?: string
}

export interface NewsletterPayload {
  subject: string
  preheader?: string
  body: string
  cta: string
}

export interface PresentationPayload {
  html_vault_path?: string
  pdf_vault_path?: string
  html_url?: string
  pdf_url?: string
  aspect?: string
  slide_count?: number
  raw_markdown?: string
  rawMarkdown?: string
}

export type OutputStreamEvent =
  | { type: 'status'; phase: string; detail?: string }
  | {
      type: 'classified'
      task_type: TaskType
      format: OutputFormat
      model: string
      platform?: PlatformSkill
      mode?: PlatformOutputMode
      presentation_format?: PresentationAspect
    }
  | { type: 'token'; content: string }
  | {
      type: 'done'
      task_type: TaskType
      vault_id: string
      vault_path: string
      storage_url?: string
      auto_image_path?: string
      json_payload?: LinkedinPostPayload | XPostPayload | PresentationPayload | Record<string, unknown>
    }
  | { type: 'error'; message: string }

export interface RunOutputOptions {
  platform: PlatformSkill
  dossier: string
  auftrag: string
  mode?: PlatformOutputMode
  topicFreshness?: boolean
  presentationFormat?: PresentationAspect
  useWebsiteStyleOverride?: boolean
  websiteStyleUrl?: string
  brand?: string
  audience?: string
  length?: string
  sessionId?: string
  primaryBrandId?: string | null
  secondaryBrandIds?: string[]
  signal?: AbortSignal
  onEvent: (event: OutputStreamEvent) => void
}

export async function streamOutput(opts: RunOutputOptions): Promise<void> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(`${baseUrl}/api/output-skills/${opts.platform}`, {
    method: 'POST',
    headers: await buildAuthedHeaders({
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    }),
    body: JSON.stringify({
      dossier: opts.dossier,
      auftrag: opts.auftrag,
      mode: opts.mode ?? 'create',
      topic_freshness: opts.topicFreshness ?? false,
      presentation_format: opts.presentationFormat ?? '16:9',
      use_website_style_override: opts.useWebsiteStyleOverride ?? false,
      website_style_url: opts.websiteStyleUrl || undefined,
      brand: opts.brand,
      audience: opts.audience,
      length: opts.length,
      session_id: opts.sessionId,
      primary_brand_id: opts.primaryBrandId ?? undefined,
      secondary_brand_ids: opts.secondaryBrandIds ?? undefined,
    }),
    signal: opts.signal,
  })

  if (!response.ok) {
    let message = response.statusText
    try {
      const body = (await response.json()) as { error?: { message?: string } }
      message = body.error?.message ?? message
    } catch {
      /* ignore */
    }
    throw new Error(message)
  }
  if (!response.body) throw new Error('Keine Stream-Antwort')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let separatorIdx = buffer.indexOf('\n\n')
      while (separatorIdx !== -1) {
        const rawEvent = buffer.slice(0, separatorIdx)
        buffer = buffer.slice(separatorIdx + 2)
        for (const line of rawEvent.split('\n')) {
          if (!line.startsWith('data:')) continue
          const payload = line.slice(5).trim()
          if (!payload) continue
          try {
            const parsed = JSON.parse(payload) as OutputStreamEvent
            opts.onEvent(parsed)
          } catch (err) {
            console.error('SSE parse error', err, payload)
          }
        }
        separatorIdx = buffer.indexOf('\n\n')
      }
    }
  } finally {
    reader.releaseLock()
  }
}

export async function getSignedUrl(path: string): Promise<string> {
  const baseUrl = env.VITE_API_URL.replace(/\/+$/, '')

  const response = await fetch(
    `${baseUrl}/api/vault/file/signed-url?path=${encodeURIComponent(path)}`,
    {
      headers: await buildAuthedHeaders(),
    },
  )
  if (!response.ok) throw new Error('Signed URL failed')
  const json = (await response.json()) as { url: string }
  return json.url
}
